import { MenuItem } from '@mui/material';
import classnames from 'classnames';
import { find } from 'lodash';
import React from 'react';
import { components } from 'react-select';
import { SchemaEditorViewModeType } from '../../constants/schemaEditor';
import { FLOW_TYPES_WITHOUT_LIVE_SAMPLES } from '../../constants/pipelineConstants';
import Tooltip from '../elements/mui/Tooltip';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  recipeBuilderSelectStyles,
  SelectInput,
  SelectOption,
} from '../elements/rselect/ReactSelectUtils';
import UnsortedSelect from '../elements/rselect/UnsortedSelect';
import AttributesAsColsIcon from './icons/AttributesAsColsIcon';
import AttributesAsJSONIcon from './icons/AttributesAsJSONIcon';
import AttributesAsRowsIcon from './icons/AttributesAsRowsIcon';
import DownloadSamplesIconComponent from './icons/DownloadSamplesIcon';
import { ReactComponent as RefreshSample } from 'new/components/icons/fi-rr-refresh-no-border.svg';
import SamplesDownloadFormatIcon from './icons/SamplesDownloadFormatIcon';
import RecipeBuilderGridControlsMenu from './RecipeBuilderGridControlsMenu';
import { SwitchLabeled } from 'new/elements/Switch/SwitchLabeled/SwitchLabeled';
import { GreySpinner } from 'new/components/icons/GreySpinner/GreySpinner';

const downloadSamplesIcon = (
  <DownloadSamplesIconComponent width={16} height={16} style={{ color: '#5F606D' }} />
);

const NUM_SAMPLES_OPTIONS = [
  {
    label: '5 cached samples',
    value: 'cached-5',
    count: 5,
    live: false,
  },
  {
    label: '10 cached samples',
    value: 'cached-10',
    count: 10,
    live: false,
  },
  {
    label: '15 cached samples',
    value: 'cached-15',
    count: 15,
    live: false,
  },
  {
    label: '20 cached samples',
    value: 'cached-20',
    count: 20,
    live: false,
  },
  {
    label: '5 live samples',
    value: 'live-5',
    count: 5,
    live: true,
  },
  {
    label: '10 live samples',
    value: 'live-10',
    count: 10,
    live: true,
  },
  {
    label: '25 live samples',
    value: 'live-25',
    count: 25,
    live: true,
  },
  {
    label: '50 live samples',
    value: 'live-50',
    count: 50,
    live: true,
  },
  {
    label: '100 live samples',
    value: 'live-100',
    count: 100,
    live: true,
  },
];
const VIEW_MODE_OPTIONS = [
  {
    label: 'Attributes as Rows',
    value: SchemaEditorViewModeType.TABLE,
  },
  {
    label: 'Attributes as JSON',
    value: SchemaEditorViewModeType.CODE,
  },
];

const recipeBuilderHeaderSelectStyles = {
  ...recipeBuilderSelectStyles,
  control: (styles) => ({
    ...styles,
    minHeight: 30,
    cursor: 'pointer',
    borderRadius: 8,
    border: '1px solid #E4E4E7',
    backgroundColor: 'white',
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    padding: 6,
    color: '#2F3037',
  }),
};

function ViewModeSingleValue(props) {
  const {
    data: { value },
  } = props;
  let content;

  switch (value) {
    case SchemaEditorViewModeType.TABLE_TRANSPOSE:
      content = (
        <div className="view-mode-single-value-content">
          <AttributesAsColsIcon />
          <div className="view-mode-single-value-content-text">Columns</div>
        </div>
      );
      break;

    case SchemaEditorViewModeType.CODE:
      content = (
        <div className="view-mode-single-value-content">
          <AttributesAsJSONIcon />
          <div className="view-mode-single-value-content-text">JSON</div>
        </div>
      );
      break;

    default:
      content = (
        <div className="view-mode-single-value-content">
          <AttributesAsRowsIcon />
          <div className="view-mode-single-value-content-text">Rows</div>
        </div>
      );
  }

  return <components.SingleValue {...props}>{content}</components.SingleValue>;
}

class RecipeBuilderGridControls extends React.PureComponent {
  state = {
    downloadMenuAnchorEl: null,
  };

  render() {
    const {
      numSamples,
      liveSamples,
      selectedViewMode,
      compactHeader,
      onNumSamplesChanged,
      onRefreshSamples,
      loadingSamples,
      dataSet,
    } = this.props;
    const { downloadMenuAnchorEl } = this.state;

    const flowType = dataSet?.flow_type || dataSet?.source_config?.['pipeline.type'];

    // Simple filtering: show all options for supported flow types, only cached for unsupported
    const availableSampleOptions = this._getAvailableSampleOptions(flowType);

    const selectedNumSamplesOption = find(availableSampleOptions, {
      count: numSamples,
      live: liveSamples,
    });
    const selectedViewModeOption = getCurrentSelectedOption(VIEW_MODE_OPTIONS, selectedViewMode);

    return (
      <div className="recipe-builder-controls">
        <div className="control-holder">
          <UnsortedSelect
            value={selectedViewModeOption}
            options={VIEW_MODE_OPTIONS}
            components={{
              Input: SelectInput,
              Option: SelectOption,
              SingleValue: ViewModeSingleValue,
            }}
            isSearchable={false}
            styles={{
              ...recipeBuilderHeaderSelectStyles,
              container: (styles) => ({
                ...styles,
                width: 160,
                fontSize: 13,
              }),
            }}
            onChange={this._handleViewModeChanged}
          />
        </div>
        {onNumSamplesChanged && (
          <div className="control-holder">
            <UnsortedSelect
              value={selectedNumSamplesOption}
              options={availableSampleOptions}
              components={{ Input: SelectInput, Option: SelectOption }}
              isSearchable={false}
              onChange={this._handleNumSamplesChanged}
              styles={{
                ...recipeBuilderHeaderSelectStyles,
                container: (styles) => ({
                  ...styles,
                  width: 175,
                  fontSize: 13,
                }),
              }}
            />
          </div>
        )}
        {!compactHeader && (
          <>
            {typeof onRefreshSamples === 'function' && (
              <div className="control-holder">
                <Tooltip title="Refresh Samples" arrow>
                  <button
                    onClick={onRefreshSamples}
                    className="recipe-builder-header-refresh-button"
                  >
                    {loadingSamples ? <GreySpinner /> : <RefreshSample width={16} height={16} />}
                  </button>
                </Tooltip>
              </div>
            )}
            <div className="control-holder">
              <Tooltip title="Download Samples" arrow={true}>
                <button
                  className={classnames('recipe-builder-header-button', {
                    active: !!downloadMenuAnchorEl,
                  })}
                  onClick={this._handleDownloadClicked}
                >
                  {downloadSamplesIcon}
                </button>
              </Tooltip>
              <RecipeBuilderGridControlsMenu
                open={!!downloadMenuAnchorEl}
                anchorEl={downloadMenuAnchorEl}
                onClose={this._handleDownloadMenuClosed}
              >
                <MenuItem
                  className="recipe-builder-menu-item"
                  onClick={() => this._handleDownloadSamples('csv')}
                >
                  <span className="icon">
                    <SamplesDownloadFormatIcon />
                  </span>
                  CSV Download
                </MenuItem>
                <MenuItem
                  className="recipe-builder-menu-item"
                  onClick={() => this._handleDownloadSamples('json')}
                >
                  <span className="icon">
                    <SamplesDownloadFormatIcon />
                  </span>
                  JSON Download
                </MenuItem>
              </RecipeBuilderGridControlsMenu>
            </div>
          </>
        )}
      </div>
    );
  }

  _getAvailableSampleOptions = (flowType) => {
    if (!flowType || !FLOW_TYPES_WITHOUT_LIVE_SAMPLES.includes(flowType)) {
      // For flow types that support live samples, show all options
      return NUM_SAMPLES_OPTIONS;
    }
    // For flow types that don't support live samples, only show cached options
    return NUM_SAMPLES_OPTIONS.filter((sample) => !sample.live);
  };

  _handleNumSamplesChanged = (selectedOption) => {
    const { count, live } = selectedOption;
    this.props.onNumSamplesChanged({ count, live });
  };

  _handleViewModeChanged = (selectedOption) => {
    this.props.onViewModeChanged(getNewSelectionValue(selectedOption, true));
  };

  _handleDownloadClicked = (e) => {
    this.setState({ downloadMenuAnchorEl: e.currentTarget });
  };

  _handleDownloadMenuClosed = () => {
    this.setState({ downloadMenuAnchorEl: null });
  };

  _handleDownloadSamples = (type) => {
    this.setState({ downloadMenuAnchorEl: null });
    this.props.onDownloadSamples(type);
  };
}

RecipeBuilderGridControls.defaultProps = {
  numSamples: 5,
  liveSamples: false,
  selectedViewMode: SchemaEditorViewModeType.TABLE,
  loadingSamples: false,
  dataSet: null,
};

export default RecipeBuilderGridControls;
